import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import { usePeriod } from '@/lib/period';
import { PeriodControl } from '@/features/shared/PeriodControl';
import { KpiCards } from './KpiCards';
import { LifetimeFreeCard } from './LifetimeFreeCard';
import { PartnerRemittanceCard } from './PartnerRemittanceCard';
import { RevenueByCurrencyTable } from './RevenueByCurrencyTable';
import { TopDaysTable } from './TopDaysTable';
import { TopRevenueAccountsTable } from './TopRevenueAccountsTable';
import { useFinanceMetrics } from './useFinanceMetrics';
import { useAnomaly } from './useAnomaly';
import { ArptChart } from './charts/ArptChart';
import { LabelsChart } from './charts/LabelsChart';
import { MrrChart } from './charts/MrrChart';
import { NetNewMrrChart } from './charts/NetNewMrrChart';
import { SalesChart } from './charts/SalesChart';
import { UsersChart } from './charts/UsersChart';

/**
 * Página `/dashboard/finance` — visão consolidada super-admin: MRR, ARPT,
 * vendas, novos usuários, etiquetas, Net New MRR e churn, com deltas vs o
 * período anterior, gráficos, receita por moeda, top contas, cortesias e
 * repasse a parceiros.
 *
 * Origem: sem issue — commit 498e432c6, 2026-05-04 15:52 +0200.
 *
 * Contrato: período vem de `usePeriod` (sincronizado na URL, `PeriodControl`);
 * `useFinanceMetrics(period)` entrega `LOADING | ERROR | READY`. Os cards
 * `LifetimeFreeCard` e `TopRevenueAccountsTable` fazem as próprias leituras;
 * os demais recebem `metrics.series`.
 *
 * Usado por: `routes/router.tsx` (`dashboard/finance`), admin web.
 *
 * Arquitetura: não lê tenant a tenant — lê os agregados diários que o
 * scheduler `aggregateDailyMetrics` (Functions) grava; o cliente só soma.
 *
 * Dados: `aggregations/daily/days/{yyyymmdd}` (`DailyAggregateSchema`,
 * `packages/types/src/aggregations.ts`), via `useDailyAggregates`; leitura
 * gateada por `isSuperAdmin()` em `match /aggregations/{type}/{subcoll}/{id}`
 * (`firebase/firestore.rules`). Valores monetários em CENTAVOS; churn é fração
 * (0–1), formatada em `KpiCards`.
 *
 * Histórico:
 *  - 2026-05-26 01:00 +0200 — card "Tenants isentos" (`LifetimeFreeCard`).
 *  - 2026-06-09 00:40 +0200 — Cloudscape (39-G).
 *  - 2026-09-25 17:32 +0200 — (#743) `PartnerRemittanceCard`.
 */
export function FinanceDashboardPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { period, setPeriod } = usePeriod();
  const state = useFinanceMetrics(period);
  const series = state.kind === 'READY' ? state.metrics.series : [];
  const anomalies = useAnomaly(series);

  return (
    <ContentLayout
      header={<Header variant="h1" description={t('finance.subtitle')}>{t('finance.title')}</Header>}
    >
      <SpaceBetween size="l">
        <PeriodControl value={period} onChange={setPeriod} />

        {state.kind === 'LOADING' && (
          <Box textAlign="center" padding="l">
            <Spinner size="large" />
          </Box>
        )}
        {state.kind === 'ERROR' && <Alert type="error">{state.error}</Alert>}
        {state.kind === 'READY' && (
          <SpaceBetween size="l">
            <KpiCards metrics={state.metrics} anomalies={anomalies} />
            <ColumnLayout columns={2}>
              <MrrChart series={state.metrics.series} />
              <NetNewMrrChart series={state.metrics.series} />
              <SalesChart series={state.metrics.series} />
              <UsersChart series={state.metrics.series} />
              <LabelsChart series={state.metrics.series} />
              <ArptChart series={state.metrics.series} />
            </ColumnLayout>
            <ColumnLayout columns={2}>
              <RevenueByCurrencyTable metrics={state.metrics} />
              <TopRevenueAccountsTable />
            </ColumnLayout>
            <ColumnLayout columns={2}>
              <LifetimeFreeCard />
              <PartnerRemittanceCard series={state.metrics.series} />
            </ColumnLayout>
            <TopDaysTable series={state.metrics.series} />
          </SpaceBetween>
        )}
      </SpaceBetween>
    </ContentLayout>
  );
}
